/**
 * Shared sanitizer for the rich-text values users type into free-text fields.
 *
 * Everything typed or pasted into a rich-text field, and everything rendered back out of one
 * (detail views, exported reports), passes through here. It is an allow-list: any tag or attribute
 * not named below is dropped, so stored content can never carry script, event handlers, inline
 * styles, embedded frames or `javascript:` URLs.
 */

/** Formatting tags the editor can produce, plus the ones commonly pasted from Word/Outlook. */
const ALLOWED_TAGS = new Set([
  'P', 'BR', 'DIV', 'SPAN',
  'STRONG', 'B', 'EM', 'I', 'U', 'S', 'STRIKE', 'SUB', 'SUP',
  'UL', 'OL', 'LI',
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'BLOCKQUOTE', 'PRE', 'CODE', 'HR', 'A',
  'TABLE', 'THEAD', 'TBODY', 'TR', 'TH', 'TD',
]);

/**
 * Tags whose text content is markup or metadata rather than something the user meant to paste.
 *
 * These are removed outright instead of being unwrapped like an unknown inline wrapper: Word and
 * Outlook put a full `<html><head><style>…` document on the clipboard, so unwrapping `<style>`
 * dumped the whole stylesheet into the field as visible text — the "it pasted the HTML" report.
 */
const DROPPED_TAGS = new Set([
  'SCRIPT', 'STYLE', 'HEAD', 'TITLE', 'META', 'LINK', 'BASE', 'NOSCRIPT', 'TEMPLATE',
  'IFRAME', 'FRAME', 'FRAMESET', 'OBJECT', 'EMBED', 'APPLET', 'PARAM',
  'XML', 'SVG', 'MATH', 'CANVAS', 'AUDIO', 'VIDEO', 'SOURCE', 'TRACK',
  'SELECT', 'DATALIST', 'OPTGROUP', 'OPTION',
]);

/** The only attributes kept, per tag. Everything else (including style and on*) is removed. */
const ALLOWED_ATTRIBUTES: Record<string, ReadonlySet<string>> = {
  A: new Set(['href', 'title']),
};

const SAFE_LINK_PROTOCOLS = ['http:', 'https:', 'mailto:'];

function isSafeHref(value: string): boolean {
  try {
    return SAFE_LINK_PROTOCOLS.includes(new URL(value, document.baseURI).protocol);
  } catch {
    return false;
  }
}

/**
 * Strips everything outside the allow-list from `html` and returns the safe remainder.
 * Parsing happens in an inert document, so nothing in the input is executed or fetched
 * while it is being cleaned.
 */
export function sanitizeRichText(html: string): string {
  if (!html) return '';

  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const body = doc.body;

  // Office puts its conditional markup in comments (`<!--[if gte mso 9]><xml>…`), and the clipboard
  // adds StartFragment/EndFragment markers. None of it is content, so it goes before anything else.
  const comments = doc.createTreeWalker(body, NodeFilter.SHOW_COMMENT);
  const stale: Comment[] = [];
  while (comments.nextNode()) stale.push(comments.currentNode as Comment);
  for (const comment of stale) comment.remove();

  // Walk a static list: the tree is mutated while iterating.
  for (const el of Array.from(body.querySelectorAll('*'))) {
    // Namespaced (SVG/MathML) elements report a lower-case tagName, so normalise before matching.
    const tag = el.tagName.toUpperCase();
    if (DROPPED_TAGS.has(tag)) {
      // Removing takes the subtree with it — deeper nodes are still in the static list but are now
      // detached, and the operations below are no-ops on a node without a parent.
      el.remove();
      continue;
    }
    if (!ALLOWED_TAGS.has(tag)) {
      // Keep the text of a disallowed wrapper (e.g. <font>), discard the wrapper itself.
      el.replaceWith(...Array.from(el.childNodes));
      continue;
    }
    const allowed = ALLOWED_ATTRIBUTES[tag];
    for (const attr of Array.from(el.attributes)) {
      if (!allowed?.has(attr.name.toLowerCase())) {
        el.removeAttribute(attr.name);
      } else if (attr.name.toLowerCase() === 'href' && !isSafeHref(attr.value)) {
        el.removeAttribute(attr.name);
      }
    }
    if (tag === 'A') {
      el.setAttribute('target', '_blank');
      el.setAttribute('rel', 'noopener noreferrer');
    }
  }

  return body.innerHTML;
}

/** True when the value carries rich-text markup rather than being plain typed text. */
export function isRichText(value: string | null | undefined): boolean {
  return !!value && /<\/?[a-z][\s\S]*>/i.test(value);
}

/** The visible text of a rich-text value, for grids, exports and empty checks. */
export function richTextToPlain(value: string | null | undefined): string {
  if (!value) return '';
  if (!isRichText(value)) return value;
  const doc = new DOMParser().parseFromString(`<body>${value}</body>`, 'text/html');
  return (doc.body.textContent ?? '').replace(/\u00a0/g, ' ').trim();
}

/**
 * Renders a stored value as safe display HTML. Plain-text values (everything captured before
 * rich text existed, and anything typed without formatting) keep their line breaks instead of
 * collapsing into one paragraph.
 */
export function richTextToDisplayHtml(value: string | null | undefined): string {
  if (!value) return '';
  const displayValue = decodeEscapedRichText(value);
  if (isRichText(displayValue)) return sanitizeRichText(displayValue);
  const div = document.createElement('div');
  div.textContent = displayValue;
  return div.innerHTML.replace(/\r?\n/g, '<br />');
}

/** Restores markup that was HTML-escaped before an earlier rich-text value was persisted. */
function decodeEscapedRichText(value: string): string {
  if (isRichText(value) || !/&lt;\/?[a-z][\s\S]*&gt;/i.test(value)) return value;
  const doc = new DOMParser().parseFromString(`<body>${value}</body>`, 'text/html');
  return doc.body.textContent ?? value;
}

/** Tags that carry actual formatting, as opposed to the structural wrappers contenteditable adds. */
const FORMATTING_TAGS =
  /<\s*(strong|b|em|i|u|s|strike|sub|sup|ul|ol|li|h[1-6]|blockquote|pre|code|hr|a|table)\b/i;

/**
 * Collapses an editor value back to plain text when the user applied no formatting.
 *
 * Contenteditable always produces markup (`<div>`, `<br>`, `<p>`) even for a plain paragraph. Storing
 * that would turn every existing description into HTML and make it render as visible tags anywhere
 * that has not been switched over to rich-text display yet. Keeping unformatted input as plain text
 * means a field only ever becomes HTML once the user has actually formatted it.
 */
export function normalizeRichTextForStorage(html: string): string {
  const safe = sanitizeRichText(html);
  if (!safe) return '';
  if (FORMATTING_TAGS.test(safe)) return safe;

  // No formatting: rebuild the plain text, turning block boundaries back into newlines.
  const doc = new DOMParser().parseFromString(`<body>${safe}</body>`, 'text/html');
  // Clipboard HTML commonly has indentation between block elements. It is not user content, but
  // textContent would preserve it as extra submitted line breaks.
  const structuralWhitespace: Text[] = [];
  const textNodes = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  while (textNodes.nextNode()) {
    const node = textNodes.currentNode as Text;
    if (/^[\t\r\n]+$/.test(node.textContent ?? '')) structuralWhitespace.push(node);
  }
  structuralWhitespace.forEach(node => node.remove());
  doc.body.querySelectorAll('br').forEach(br => br.replaceWith('\n'));
  // Browser and clipboard markup may wrap ordinary words in separate blocks even when the user
  // did not create paragraphs. Intentional Enter presses are stored as <br>, so only they become
  // a persisted line break; structural block boundaries collapse to a normal space.
  doc.body.querySelectorAll('div, p').forEach(block => block.after(' '));
  return (doc.body.textContent ?? '').replace(/\u00a0/g, ' ').replace(/[ \t]+\n/g, '\n').replace(/\n[ \t]+/g, '\n').replace(/ {2,}/g, ' ').replace(/\n{3,}/g, '\n\n').trim();
}
